export const site = {
  name: 'Zuri Hub',
  tagline: 'CX Software where every Business Conversation Happens',
  baseUrl: 'https://zurihub-website-6t7.pages.dev',
  workspaceUrl: 'https://zurichat-business-88t.pages.dev',
  description:
    'Zuri Hub unifies live chat, tickets, knowledge base, omnichannel messaging, AI voice, and analytics in one modern customer experience platform.',
  twitter: 'https://x.com/zurihub',
  linkedin: 'https://www.linkedin.com/company/zuri-chat/',
  youtube: 'https://www.youtube.com/@bahatiasher_faith?sub_confirmation=1',
  github: 'https://github.com/appnomu/Zurichat',
  ogImage: '/og-image.png',
};

export const navLinks = [
  { label: 'Home', path: '/' },
  { label: 'Product', path: '/product' },
  { label: 'Features', path: '/features' },
  { label: 'Pricing', path: '/pricing' },
  {
    label: 'Solutions',
    path: '/solutions',
    children: [
      { label: 'Industries', items: [
        { label: 'E-commerce', path: '/industry/ecommerce' },
        { label: 'Services', path: '/industry/services' },
        { label: 'Education', path: '/industry/education' },
        { label: 'Health', path: '/industry/health' },
        { label: 'Finance', path: '/industry/finance' },
        { label: 'SaaS', path: '/industry/saas' },
        { label: 'Travel', path: '/industry/travel' },
      ]},
      { label: 'Teams', items: [
        { label: 'Customer Service', path: '/solutions/customer-service' },
        { label: 'Marketing & Sales', path: '/solutions/marketing-and-sales' },
      ]},
    ],
  },
  { label: 'Why Zuri', path: '/why-zuri' },
  { label: 'Referral', path: '/referral' },
  { label: 'Book a Demo', path: '/book-a-demo' },
];

export const footerLinks = {
  product: [
    { label: 'Product', path: '/product' },
    { label: 'Features', path: '/features' },
    { label: 'Pricing', path: '/pricing' },
    { label: 'Book a Demo', path: '/book-a-demo' },
  ],
  company: [
    { label: 'About Zuri Hub', path: '/about' },
    { label: 'Help Centre', path: 'https://business.zurichat.app/kb?workspaceSlug=zurichat' },
    { label: 'Careers', path: '/careers' },
    { label: 'Contact', path: '/book-a-demo' },
  ],
  legal: [
    { label: 'Terms of Use', path: '/terms' },
    { label: 'Privacy Policy', path: '/privacy' },
    { label: 'Cookie Preferences', path: '/cookie-notice' },
  ],
};

export const products = [
  {
    id: 'zyra-ai',
    name: 'Zyra AI',
    tagline: 'AI answers, summaries, and coaching',
    description:
      'Zyra suggests replies, summarizes chats, detects intent, and trains agents in real time. Every support rep becomes a top performer.',
    benefits: [
      'AI reply suggestions from your docs',
      'Instant conversation summaries',
      'Sentiment and intent detection',
      'Real-time agent coaching',
    ],
    cta: 'Explore Zyra AI',
  },
  {
    id: 'zuri-cloud',
    name: 'Zuri Cloud',
    tagline: 'Secure, global infrastructure',
    description:
      'The backbone that hosts every conversation, file, and metric. Built for scale, GDPR-ready, and backed by a 99.99% uptime SLA.',
    benefits: [
      '99.99% uptime SLA',
      'Global edge delivery',
      'SOC 2 and GDPR ready',
      'Auto-scales as you grow',
    ],
    cta: 'Explore Zuri Cloud',
  },
  {
    id: 'zuri-voice',
    name: 'Zuri Voice',
    tagline: 'AI voice calls inside the inbox',
    description:
      'Make, receive, transcribe, and route AI voice calls from the same workspace. No separate phone tool needed.',
    benefits: [
      'Inbound and outbound calls',
      'Real-time transcription',
      'Ticket and CRM context sync',
      'Call recording and QA insights',
    ],
    cta: 'Explore Zuri Voice',
  },
];

export const features = [
  {
    title: 'Unified Inbox',
    description: 'One stream for every channel. Email, chat, WhatsApp, Telegram, and social in one view.',
    icon: 'Inbox',
  },
  {
    title: 'Live Chat',
    description: 'Embed a fast chat widget and convert visitors while they are still on your site.',
    icon: 'MessageSquare',
  },
  {
    title: 'Ticket Management',
    description: 'Assign, tag, prioritize, and automate tickets so nothing falls through the cracks.',
    icon: 'Ticket',
  },
  {
    title: 'Knowledge Base',
    description: 'Publish public help centers and private wikis that customers and agents can search.',
    icon: 'BookOpen',
  },
  {
    title: 'AI Assistant',
    description: 'Zyra drafts replies, summarizes threads, and flags urgent conversations instantly.',
    icon: 'Sparkles',
  },
  {
    title: 'AI Voice',
    description: 'Handle and transcribe voice calls without leaving Zuri Hub.',
    icon: 'Phone',
  },
  {
    title: 'Canned Responses',
    description: 'Insert pre-written replies in one click to cut first-response time.',
    icon: 'MessageCircle',
  },
  {
    title: 'Live Typing',
    description: 'See what the customer is typing before they hit send and reply faster.',
    icon: 'Pencil',
  },
  {
    title: 'Analytics',
    description: 'Track resolution times, CSAT, channel mix, and team performance in real time.',
    icon: 'BarChart3',
  },
  {
    title: 'Team Notes',
    description: 'Add private notes to any conversation without cluttering the customer thread.',
    icon: 'StickyNote',
  },
];

export const workspaceFeatures = [
  {
    category: 'Conversations',
    summary: 'Talk to customers on every channel from one workspace.',
    features: [
      {
        title: 'Conversations',
        description: 'A unified inbox that brings live chat, email, and social messages into one thread.',
        icon: 'MessageSquare',
      },
      {
        title: 'Channels',
        description: 'Connect WhatsApp, Instagram, Telegram, X, Messenger, Gmail, and live chat instantly.',
        icon: 'Hash',
      },
      {
        title: 'Canned Responses',
        description: 'Insert saved replies with one click to cut first-response time.',
        icon: 'MessageCircle',
      },
      {
        title: 'Live Typing',
        description: 'See what customers are typing before they hit send and answer faster.',
        icon: 'Pencil',
      },
    ],
  },
  {
    category: 'Customer Success',
    summary: 'Convert, support, and retain customers at every stage.',
    features: [
      {
        title: 'Tickets',
        description: 'Create, assign, tag, and resolve customer issues with custom workflows.',
        icon: 'Ticket',
      },
      {
        title: 'Knowledge Base',
        description: 'Publish public help centers and internal wikis that agents can search in one click.',
        icon: 'BookOpen',
      },
      {
        title: 'Leads',
        description: 'Capture prospects, score them, and hand them off to sales or support.',
        icon: 'Target',
      },
      {
        title: 'Refer & Earn',
        description: 'Grow through a built-in referral program and track every invite.',
        icon: 'Gift',
      },
    ],
  },
  {
    category: 'AI & Automation',
    summary: 'Let Zyra handle the routine so your team can focus on what matters.',
    features: [
      {
        title: 'AI Configure',
        description: 'Set up Zyra AI with your knowledge base, tone, and escalation rules.',
        icon: 'Sparkles',
      },
      {
        title: 'AI Training',
        description: 'Fine-tune Zyra on past conversations to improve reply quality over time.',
        icon: 'Sparkles',
      },
      {
        title: 'AI Voice Call',
        description: 'Make, receive, and transcribe AI voice calls without leaving the inbox.',
        icon: 'Phone',
      },
    ],
  },
  {
    category: 'Team & Docs',
    summary: 'Keep your team aligned and your documentation close.',
    features: [
      {
        title: 'Team',
        description: 'Manage members, roles, and availability across shifts.',
        icon: 'Users',
      },
      {
        title: 'Docx',
        description: 'Create, share, and edit internal documents and SOPs.',
        icon: 'FileText',
      },
      {
        title: 'Team Notes',
        description: 'Leave private notes on any conversation without cluttering the customer thread.',
        icon: 'StickyNote',
      },
    ],
  },
  {
    category: 'Operations',
    summary: 'Run the business side of customer experience with confidence.',
    features: [
      {
        title: 'Invoices',
        description: 'Generate and track invoices for services, products, or subscriptions.',
        icon: 'Receipt',
      },
      {
        title: 'Billing & Plan',
        description: 'Manage seats, payment methods, and usage in one place.',
        icon: 'CreditCard',
      },
      {
        title: 'My Account',
        description: 'Update profile, security, and workspace preferences.',
        icon: 'User',
      },
      {
        title: 'Settings',
        description: 'Configure workflows, permissions, integrations, and brand settings.',
        icon: 'Settings',
      },
    ],
  },
  {
    category: 'Analytics',
    summary: 'Measure what matters and improve every day.',
    features: [
      {
        title: 'Analytics',
        description: 'Track resolution times, CSAT, channel mix, and team performance in real time.',
        icon: 'BarChart3',
      },
    ],
  },
];

export const useCases = [
  {
    title: 'Customer Support',
    icon: 'MessageCircle',
    description:
      'Resolve tickets, answer live chat, and deflect FAQs with AI — all from one inbox.',
  },
  {
    title: 'Sales & Pre-Sales',
    icon: 'Target',
    description:
      'Capture leads on WhatsApp and Instagram, qualify them, and hand them off to sales.',
  },
  {
    title: 'Technical Operations',
    icon: 'Zap',
    description:
      'Track incidents, manage docs, and align engineering, support, and product teams.',
  },
  {
    title: 'Billing & Accounts',
    icon: 'CreditCard',
    description:
      'Handle invoices, plan questions, and payment issues without bouncing customers between tools.',
  },
];

export const serviceQuality = [
  {
    metric: '99.99%',
    label: 'Uptime SLA',
    description: 'Built on Zuri Cloud with global edge delivery.',
  },
  {
    metric: '<200ms',
    label: 'Median API response',
    description: 'Fast APIs keep your agents and customers moving.',
  },
  {
    metric: 'SOC 2 / GDPR',
    label: 'Compliance ready',
    description: 'Encryption, access controls, and privacy by design.',
  },
  {
    metric: '24/7',
    label: 'Support on Enterprise',
    description: 'Priority support for Pro and dedicated help for Enterprise.',
  },
];

export const scalability = {
  title: 'Built to scale with your business',
  subtitle: 'From your first agent to your thousandth.',
  tiers: [
    {
      agents: '1',
      plan: 'Free',
      capabilities:
        'Core chat, tickets, and knowledge base for one agent.',
    },
    {
      agents: '5-100',
      plan: 'Pro',
      capabilities:
        'Unlimited agents, all channels, AI replies, canned responses, and analytics.',
    },
    {
      agents: '100+',
      plan: 'Enterprise',
      capabilities:
        'Custom SLAs, SSO, AI voice, private cloud, and 24/7 support.',
    },
  ],
};

export const featureHighlights = [
  {
    id: 'security',
    title: 'Enterprise-ready security',
    icon: 'Shield',
    summary:
      'Keep customer data safe with encryption, access controls, and compliance built in from day one.',
    items: [
      'GDPR-ready data handling',
      'SOC 2 Type II in progress',
      'End-to-end encryption at rest and in transit',
      'SSO and role-based permissions on Enterprise',
    ],
  },
  {
    id: 'brand',
    title: 'Designed for your brand',
    icon: 'Palette',
    summary:
      'Make every customer touchpoint feel like your own product, not a third-party tool.',
    items: [
      'Custom domain for your help center',
      'Branded live chat widget',
      'White-label workspace experience',
      'Custom CSS and brand colors',
    ],
  },
];

export const integrations = [
  { name: 'WhatsApp', icon: 'MessageCircle', color: 'bg-green-500' },
  { name: 'Instagram', icon: 'Instagram', color: 'bg-pink-500' },
  { name: 'Telegram', icon: 'Send', color: 'bg-sky-500' },
  { name: 'X', icon: 'X', color: 'bg-slate-900' },
  { name: 'Messenger', icon: 'MessageCircle', color: 'bg-blue-500' },
  { name: 'ZuriChat', icon: 'MessageSquare', color: 'bg-brand-600' },
  { name: 'Google Analytics', icon: 'BarChart3', color: 'bg-orange-500' },
];

export const channels = [
  { name: 'WhatsApp', brand: 'WhatsApp', color: '#25D366' },
  { name: 'Instagram', brand: 'Instagram', color: '#E1306C' },
  { name: 'Telegram', brand: 'Telegram', color: '#0088cc' },
  { name: 'X', brand: 'X', color: '#000000' },
  { name: 'Messenger', brand: 'Messenger', color: '#0084FF' },
  { name: 'ZuriChat', brand: 'ZuriChat', color: '#2563eb' },
  { name: 'Gmail', brand: 'Gmail', color: '#EA4335' },
  { name: 'Live Chat', brand: 'LiveChat', color: '#8b5cf6' },
  { name: 'Email', brand: 'Email', color: '#6366f1' },
];

export const industries = [
  {
    name: 'E-commerce',
    icon: 'ShoppingBag',
    stat: '40% faster replies',
    description: 'Handle order questions, refunds, and shipping across WhatsApp, Instagram, and live chat.',
  },
  {
    name: 'SaaS',
    icon: 'Cloud',
    stat: '30% lower churn',
    description: 'Turn trials into customers with proactive chat, tickets, and self-serve docs.',
  },
  {
    name: 'Healthcare',
    icon: 'HeartPulse',
    stat: 'HIPAA-ready tools',
    description: 'Schedule, answer patient questions, and keep teams aligned with secure messaging.',
  },
  {
    name: 'Fintech',
    icon: 'Landmark',
    stat: '99.99% uptime SLA',
    description: 'Support account holders across chat, email, and voice with full audit trails.',
  },
  {
    name: 'Education',
    icon: 'GraduationCap',
    stat: 'One help desk',
    description: 'Admissions, student support, and parent queries in a single shared inbox.',
  },
  {
    name: 'Logistics',
    icon: 'Truck',
    stat: 'Real-time tracking',
    description: 'Update drivers, customers, and partners from any channel without switching apps.',
  },
];

export const customerStats = [
  { value: '250+', label: 'teams use Zuri Hub' },
  { value: '10M+', label: 'conversations handled' },
  { value: '99.99%', label: 'average uptime' },
  { value: '2x', label: 'faster resolution' },
];

export const customerLogos = [
  'E-commerce brands in 12 countries',
  'Top 100 SaaS startups in Africa',
  'Health clinics across 3 continents',
  'Fintechs processing $2B+ monthly',
];

export const pricingPlans = [
  {
    name: 'Free',
    price: '$0',
    period: 'forever',
    description: 'For small teams trying Zuri Hub.',
    features: [
      '1 agent',
      '100 tickets / month',
      'Live chat widget',
      'Basic knowledge base',
      '7-day support history',
      'Community support',
    ],
    cta: 'Start Free',
    href: '/book-a-demo',
    featured: false,
  },
  {
    name: 'Pro',
    price: '$25',
    period: 'per agent / month',
    description: 'For growing teams that need omnichannel CX.',
    features: [
      'Unlimited agents',
      'Unlimited tickets',
      'All channels: WhatsApp, Instagram, Telegram, X, Messenger',
      'Knowledge base with custom domain',
      'AI reply suggestions (Zyra AI)',
      'Canned responses & live typing',
      'Priority support',
    ],
    cta: 'Start Pro Trial',
    href: '/book-a-demo',
    featured: true,
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    period: 'per month',
    description: 'For organizations with advanced security and scale needs.',
    features: [
      'Everything in Pro',
      'Zuri Voice AI calls',
      'Custom SLAs & uptime',
      'Dedicated success manager',
      'SSO and advanced roles',
      'On-premise or private cloud options',
      '24/7 phone support',
    ],
    cta: 'Contact Sales',
    href: '/book-a-demo',
    featured: false,
  },
];

export const calculator = {
  title: 'How much can you save with Zuri Hub?',
  subtitle: 'Compare Zuri Hub at $25/seat to the tools you already pay for.',
  zuriPrice: 25,
  competitors: [
    { name: 'Intercom', price: 99, color: 'bg-slate-900' },
    { name: 'Zendesk', price: 99, color: 'bg-emerald-600' },
    { name: 'Front', price: 59, color: 'bg-indigo-500' },
    { name: 'Gorgias', price: 60, color: 'bg-rose-500' },
  ],
};

export const migration = {
  title: 'Migrate from any support stack',
  subtitle: 'We make it easy to move your team, tickets, and contacts into Zuri Hub.',
  from: ['Intercom', 'Zendesk', 'Front', 'Freshdesk', 'Gorgias', 'Help Scout'],
  benefits: [
    'Free migration support on Pro and Enterprise',
    'Keep conversation history and tags',
    'Import users, contacts, and knowledge base',
    'Get set up in days, not months',
  ],
};

export const comparison = {
  title: 'Why teams choose Zuri Hub',
  rows: [
    { feature: 'Unified inbox (chat, email, social)', zurihub: true, intercom: false, zendesk: 'partial' },
    { feature: 'AI reply suggestions', zurihub: true, intercom: true, zendesk: 'partial' },
    { feature: 'Built-in AI voice calls', zurihub: true, intercom: false, zendesk: false },
    { feature: 'Free plan for 1 agent', zurihub: true, intercom: false, zendesk: false },
    { feature: 'Starting price per agent', zurihub: '$25', intercom: '$99', zendesk: '$99' },
    { feature: 'GDPR & SOC 2 ready', zurihub: true, intercom: true, zendesk: true },
    { feature: 'Native WhatsApp & Telegram', zurihub: true, intercom: 'partial', zendesk: 'add-on' },
    { feature: 'Open API & webhooks', zurihub: true, intercom: true, zendesk: true },
  ],
};

export const faqs = [
  {
    question: 'What can I do with Zuri Hub?',
    answer:
      'Run your entire customer experience from one workspace: live chat, tickets, knowledge base, omnichannel messaging, AI voice calls, analytics, billing, and more.',
  },
  {
    question: 'Which channels are supported?',
    answer:
      'WhatsApp, Instagram, Telegram, Messenger, X, email, Gmail, live chat, and our own ZuriChat channel.',
  },
  {
    question: 'How does Zyra AI help my team?',
    answer:
      'Zyra drafts replies, summarizes long threads, detects customer sentiment, and escalates urgent issues to the right person.',
  },
  {
    question: 'Can I migrate from Zendesk or Intercom?',
    answer:
      'Yes. We offer free migration support on Pro and Enterprise plans. You keep conversation history, contacts, tags, and your knowledge base.',
  },
  {
    question: 'Is there a free plan?',
    answer:
      'Yes. The Free plan covers one agent, 100 tickets per month, live chat, a basic knowledge base, and community support.',
  },
  {
    question: 'What industries use Zuri Hub?',
    answer:
      'E-commerce, SaaS, healthcare, fintech, education, and logistics teams use Zuri Hub to unify customer conversations and cut costs.',
  },
  {
    question: 'How do I contact sales for Enterprise?',
    answer:
      'Use the Book a Demo page and select Enterprise. Our team will reply within one business day.',
  },
];

export const solutionIndustries = [
  {
    slug: 'ecommerce',
    name: 'E-commerce',
    icon: 'ShoppingBag',
    metaTitle: 'E-commerce Customer Support Software | Zuri Hub',
    metaDescription:
      'Manage orders, returns, and shopper conversations across WhatsApp, Instagram, live chat, and email from one workspace.',
    headline: 'Turn every shopper into a repeat buyer',
    summary:
      'From checkout questions to delivery updates, Zuri Hub unifies every customer touchpoint so your team can sell more and support faster.',
    painPoints: [
      { title: 'Cart abandonment', text: 'Answer WhatsApp and live-chat questions while shoppers are still browsing.' },
      { title: 'Refund delays', text: 'Track order issues, assign tickets, and update customers in one thread.' },
      { title: 'Channel chaos', text: 'Email, social, and chat messages land in the same inbox.' },
    ],
    benefits: [
      'Unified inbox for all shopper channels',
      'AI replies trained on your product catalog',
      'Order and invoice tracking inside tickets',
      'Shared team notes on every conversation',
    ],
    cta: 'See Zuri Hub for e-commerce',
  },
  {
    slug: 'services',
    name: 'Services',
    icon: 'Briefcase',
    metaTitle: 'Customer Service Software for Service Businesses | Zuri Hub',
    metaDescription:
      'Manage client requests, appointments, and project updates across chat, email, and tickets for service companies.',
    headline: 'Deliver a client experience that wins referrals',
    summary:
      'Professional services teams use Zuri Hub to keep client conversations, quotes, appointments, and follow-ups in one place.',
    painPoints: [
      { title: 'Missed leads', text: 'Capture WhatsApp and Instagram leads before they go cold.' },
      { title: 'Slow quotes', text: 'Share estimates and answer questions in the same thread.' },
      { title: 'No history', text: 'Every client interaction is logged and searchable.' },
    ],
    benefits: [
      'One inbox for every client channel',
      'Appointment and quote tracking',
      'AI drafts for fast follow-ups',
      'Team notes on every client thread',
    ],
    cta: 'See Zuri Hub for services',
  },
  {
    slug: 'education',
    name: 'Education',
    icon: 'GraduationCap',
    metaTitle: 'Education Help Desk Software | Zuri Hub',
    metaDescription:
      'Admissions, student support, and parent queries handled in a single shared inbox with AI and knowledge base tools.',
    headline: 'Support students, parents, and staff in one place',
    summary:
      'Schools, universities, and edtech teams use Zuri Hub to handle admissions, IT help, and parent questions across every channel.',
    painPoints: [
      { title: 'Siloed departments', text: 'Admissions, IT, and finance use one shared inbox.' },
      { title: 'Repeated questions', text: 'Deflect FAQs with a public knowledge base and AI replies.' },
      { title: 'Slow responses', text: 'Route urgent student issues to the right team instantly.' },
    ],
    benefits: [
      'Shared inbox for every department',
      'Public help centers for students and parents',
      'AI replies for common questions',
      'Custom routing by topic or urgency',
    ],
    cta: 'See Zuri Hub for education',
  },
  {
    slug: 'health',
    name: 'Health',
    icon: 'HeartPulse',
    metaTitle: 'HIPAA-Ready Healthcare Support | Zuri Hub',
    metaDescription:
      'Schedule appointments, answer patient questions, and keep care teams aligned with secure, compliant messaging.',
    headline: 'Patient-first communication at scale',
    summary:
      'Clinics, telehealth, and health tech teams use Zuri Hub to coordinate appointments, patient queries, and follow-ups securely.',
    painPoints: [
      { title: 'Privacy risk', text: 'Control access with role-based permissions and encrypted channels.' },
      { title: 'Missed appointments', text: 'Send reminders and schedule updates via WhatsApp or SMS.' },
      { title: 'Slow triage', text: 'Route patient messages to the right caregiver instantly.' },
    ],
    benefits: [
      'Secure, role-based messaging',
      'Appointment and reminder workflows',
      'HIPAA-ready privacy controls',
      'Care team notes and handoffs',
    ],
    cta: 'See Zuri Hub for health',
  },
  {
    slug: 'finance',
    name: 'Finance',
    icon: 'Landmark',
    metaTitle: 'Fintech Customer Support | Zuri Hub',
    metaDescription:
      'Support account holders across chat, email, and voice with full audit trails and 99.99% uptime.',
    headline: 'Trusted support for regulated finance',
    summary:
      'Banks, fintechs, and payment providers use Zuri Hub to resolve account questions, disputes, and payment issues with full audit trails.',
    painPoints: [
      { title: 'Compliance pressure', text: 'Every conversation is logged and searchable for audit.' },
      { title: 'Fraud alerts', text: 'Route urgent security issues to the right team in seconds.' },
      { title: 'Channel overload', text: 'One inbox for in-app chat, email, and WhatsApp.' },
    ],
    benefits: [
      'Full conversation audit trail',
      'Role-based access for agents and admins',
      'AI voice and secure messaging',
      '99.99% uptime SLA on Enterprise',
    ],
    cta: 'See Zuri Hub for finance',
  },
  {
    slug: 'saas',
    name: 'SaaS',
    icon: 'Cloud',
    metaTitle: 'SaaS Customer Support & Sales | Zuri Hub',
    metaDescription:
      'Turn trials into customers with proactive chat, tickets, and self-serve docs for SaaS teams.',
    headline: 'Convert and retain more SaaS customers',
    summary:
      'SaaS teams use Zuri Hub to support trials, onboard users, and reduce churn with live chat, tickets, AI, and a knowledge base.',
    painPoints: [
      { title: 'Trial drop-off', text: 'Reach out via live chat and WhatsApp while users are evaluating.' },
      { title: 'Feature confusion', text: 'Answer product questions with AI and in-app help.' },
      { title: 'Support bottlenecks', text: 'Scale from one agent to hundreds without changing tools.' },
    ],
    benefits: [
      'Proactive chat for trials and onboarding',
      'Product knowledge base for self-serve',
      'AI reply suggestions for agents',
      'Scalable per-agent pricing',
    ],
    cta: 'See Zuri Hub for SaaS',
  },
  {
    slug: 'travel',
    name: 'Travel',
    icon: 'Plane',
    metaTitle: 'Travel & Hospitality Customer Support | Zuri Hub',
    metaDescription:
      'Handle bookings, changes, and traveler questions across WhatsApp, email, live chat, and social in one workspace.',
    headline: 'Traveler support that never misses a beat',
    summary:
      'Travel agencies, hotels, and booking platforms use Zuri Hub to manage reservations, cancellations, and guest questions in real time.',
    painPoints: [
      { title: 'Time-sensitive changes', text: 'Answer WhatsApp and live chat while travelers are on the move.' },
      { title: 'Booking confusion', text: 'Pull booking context into every ticket and reply.' },
      { title: 'Guest reviews', text: 'Resolve issues quickly to earn better ratings.' },
    ],
    benefits: [
      'Real-time chat for urgent bookings',
      'Shared inbox for WhatsApp, email, and social',
      'Booking and itinerary context in tickets',
      'AI replies for common travel questions',
    ],
    cta: 'See Zuri Hub for travel',
  },
];

export const solutionTeams = [
  {
    slug: 'customer-service',
    name: 'Customer Service',
    icon: 'Headset',
    metaTitle: 'Customer Service Software by Zuri Hub',
    metaDescription:
      'Resolve tickets, live chat, and social messages from one workspace with AI coaching and omnichannel tools.',
    headline: 'Support that keeps customers coming back',
    summary:
      'Zuri Hub gives customer service teams a single workspace for tickets, live chat, WhatsApp, email, AI replies, and self-serve knowledge bases.',
    painPoints: [
      { title: 'Siloed channels', text: 'Email, chat, and social messages live in one inbox.' },
      { title: 'Slow responses', text: 'AI suggests replies and automates common questions.' },
      { title: 'No context', text: 'Customer history, orders, and tickets are visible in every thread.' },
    ],
    benefits: [
      'Unified inbox across all channels',
      'AI reply suggestions and summaries',
      'Knowledge base deflection',
      'Ticket routing, SLAs, and team notes',
    ],
    cta: 'See customer service tools',
  },
  {
    slug: 'marketing-and-sales',
    name: 'Marketing & Sales',
    icon: 'TrendingUp',
    metaTitle: 'Marketing & Sales Messaging by Zuri Hub',
    metaDescription:
      'Capture leads on WhatsApp, Instagram, and live chat, then hand them off faster with Zuri Hub.',
    headline: 'Convert conversations into revenue',
    summary:
      'Marketing and sales teams use Zuri Hub to capture, qualify, and route leads from WhatsApp, Instagram, live chat, and email.',
    painPoints: [
      { title: 'Lost leads', text: 'Capture inbound DMs and chat before competitors do.' },
      { title: 'Slow handoffs', text: 'Qualify and assign leads to sales in one click.' },
      { title: 'No tracking', text: 'See every lead, campaign, and touchpoint in one place.' },
    ],
    benefits: [
      'Lead capture from every channel',
      'Qualification workflows and tags',
      'CRM-style lead notes and history',
      'Shared inbox with support for context',
    ],
    cta: 'See marketing & sales tools',
  },
];

export const legal = {
  terms: {
    title: 'Terms of Use',
    lastUpdated: 'August 7, 2026',
    sections: [
      {
        title: '1. Introduction',
        paragraphs: [
          'These Terms of Use govern your access to and use of the ZuriHub website, applications, and services operated by Zurichat Technology Company Limited. By creating an account, accessing the platform, or using any part of the service, you agree to be bound by these Terms and our Privacy Policy.',
          'If you are using ZuriHub on behalf of an organization, you represent that you have authority to bind that organization to these Terms. If you do not agree, please do not use the Service.',
        ],
      },
      {
        title: '2. Definitions',
        paragraphs: [
          'ZuriHub, we, us, and our refer to Zurichat Technology Company Limited.',
          'Service means the ZuriHub software, website, and related support.',
          'You means the individual or entity using the Service.',
          'Customer Data means all data, messages, contacts, tickets, invoices, and content you or your team submit through the Service.',
          'Workspace means a business account created on ZuriHub where Customer Data is stored and managed.',
        ],
      },
      {
        title: '3. Accounts and Registration',
        paragraphs: [
          'To use most features, you must register. We support email and password registration and OAuth sign-in through Google and LinkedIn. You must provide accurate, current, and complete information.',
          'You are responsible for keeping your password and session token confidential and for all activity under your account. We recommend using a strong, unique password and enabling any security features we provide.',
          'We email a four-digit verification code that expires in five minutes. Password reset links and codes expire within one hour. Sessions are valid for up to 30 days.',
          'We may suspend or terminate accounts that violate these Terms, pose security risks, or provide false information.',
          'Business workspaces use role-based access: owner, admin, and agent. Owners control billing, seat purchases, and member invitations. You may only use the Service through the role and permissions assigned to you.',
        ],
      },
      {
        title: '4. Acceptable Use',
        paragraphs: [
          'You may use the Service only for lawful purposes and in accordance with these Terms.',
          'You agree not to use the Service to transmit illegal content, harass others, distribute malware, attempt unauthorized access, send spam, or violate the rights of any third party.',
          'You may not reverse engineer, scrape, interfere with, or overload the Service except as permitted by applicable law.',
          'You are responsible for ensuring that your use of Customer Data, including customer conversations and invoices, complies with applicable laws.',
        ],
      },
      {
        title: '5. Customer Data and Content',
        paragraphs: [
          'You retain ownership of all Customer Data, including messages, contacts, tickets, knowledge base articles, customer invoices, and workspace settings.',
          'By using the Service, you grant us a limited license to host, process, transmit, and back up this data solely to provide, support, and improve the Service, and to keep logs required for security and audit purposes.',
          'You are responsible for the accuracy, quality, and legality of Customer Data and for obtaining any consents required to process it.',
          'We do not use your Customer Data to train general AI models or for advertising without your consent. AI-assisted features process data for your own workspace and do not train shared models.',
          'Activity logs are generated when users act inside a workspace. These logs include the action, timestamp, IP address, and user agent, and are stored to support audit, security, and debugging.',
        ],
      },
      {
        title: '6. Fees, Billing, Cancellation, and Invoices',
        paragraphs: [
          'Some features require payment. Business workspace subscriptions are billed per agent seat on a monthly or annual basis.',
          'A 14-day free trial is available for new workspaces without a credit card. The trial may be extended once by seven days at the owner request. After the trial ends, payment is required to continue.',
          'Pricing is quoted in the selected currency and may include taxes and discounts. Payments are processed by Flutterwave. We do not store full payment card numbers; Flutterwave collects and processes that information under its own terms.',
          'You may upgrade or add seats at any time. Additional seats are billed through the payment flow. Seat reductions take effect at the end of the current billing cycle.',
          'You may cancel through your workspace settings. Cancellation takes effect at the end of the current billing period, and the workspace may then move to a suspended or expired state.',
          'If payment is not received by the due date, the workspace becomes overdue. Overdue workspaces may be suspended after a 14-day grace period and may eventually be deleted. We do not refund fees except where required by law.',
          'The Service includes an invoice generator for your customers. You are responsible for the accuracy of customer invoices you create and send. We store invoice records on your behalf but are not responsible for payment collection between you and your customers.',
        ],
      },
      {
        title: '7. Intellectual Property',
        paragraphs: [
          'ZuriHub and all related code, designs, trademarks, logos, and content are the intellectual property of Zurichat Technology Company Limited or its licensors.',
          'You may not copy, modify, distribute, or create derivative works of our intellectual property except as expressly authorized.',
          'Feedback you provide may be used by us without restriction or compensation.',
        ],
      },
      {
        title: '8. Confidentiality and Security',
        paragraphs: [
          'We treat Customer Data as confidential and use reasonable technical and organizational measures to protect it.',
          'You are responsible for securing your account, using strong passwords, and ensuring your team follows best practices.',
        ],
      },
      {
        title: '9. Third-Party Integrations',
        paragraphs: [
          'The Service integrates with third parties such as Google and LinkedIn for OAuth authentication, Flutterwave for payment processing, Infobip for email delivery, and WhatsApp for messaging.',
          'Your use of these services is subject to the third party own terms and privacy policies. We are not responsible for their availability, accuracy, security, or practices.',
          'We only share the data necessary to enable each integration.',
        ],
      },
      {
        title: '10. Limitation of Liability',
        paragraphs: [
          'To the fullest extent permitted by law, Zurichat Technology Company Limited will not be liable for any indirect, incidental, special, consequential, or punitive damages, including lost profits or data, arising from your use of the Service.',
          'Our total liability will not exceed the amount you paid to us in the twelve months before the event giving rise to the claim, or one hundred dollars, whichever is greater.',
        ],
      },
      {
        title: '11. Indemnification',
        paragraphs: [
          'You agree to indemnify and hold harmless ZuriHub, its affiliates, officers, and employees from any claims, damages, or expenses arising from your use of the Service, your Customer Data, or your violation of these Terms.',
        ],
      },
      {
        title: '12. Termination',
        paragraphs: [
          'We may suspend or terminate your access if you breach these Terms, fail to pay applicable fees, or if required by law. Suspended workspaces cannot be accessed until payment is made or the issue is resolved.',
          'You may cancel your subscription or delete your account at any time.',
          'When you request account deletion, we schedule redaction after a 30-day grace period. During this period the account is deactivated. At the end of the grace period we anonymize personal data such as name, email, phone, avatar, and password, and cancel active subscriptions. Billing and transaction records may be retained for legal and financial record-keeping.',
          'Upon termination, your right to use the Service ceases and we may delete or anonymize Customer Data in accordance with our Privacy Policy and applicable law.',
        ],
      },
      {
        title: '13. Governing Law and Disputes',
        paragraphs: [
          'These Terms are governed by the laws of the Republic of Uganda, without regard to its conflict of laws provisions.',
          'Any dispute arising under these Terms will be resolved in the courts of Uganda, unless otherwise agreed in writing.',
        ],
      },
      {
        title: '14. Changes to These Terms',
        paragraphs: [
          'We may modify these Terms from time to time. We will notify you of material changes by email or through the Service.',
          'Continued use of the Service after changes take effect means you accept the revised Terms.',
        ],
      },
      {
        title: '15. Contact Us',
        paragraphs: [
          'For questions about these Terms, please contact us at legal@zurihub.io or through the Book a Demo page.',
        ],
      },
    ],
  },
  privacy: {
    title: 'Privacy Policy',
    lastUpdated: 'August 7, 2026',
    sections: [
      {
        title: '1. Overview',
        paragraphs: [
          'This Privacy Policy explains how Zurichat Technology Company Limited collects, uses, stores, shares, and protects your information when you use the ZuriHub website and services.',
          'We are committed to protecting your privacy and being transparent about our practices. This policy is written in plain language so you can understand what data we collect, why we collect it, and how we use it.',
          'If you have any questions after reading this policy, contact us at support@zurihub.io.',
        ],
      },
      {
        title: '2. Who We Are',
        paragraphs: [
          'Zurichat Technology Company Limited, operating as ZuriHub, is the data controller for the information described in this policy.',
          'We are registered in the Republic of Uganda and provide customer experience and support software to teams around the world.',
        ],
      },
      {
        title: '3. Information We Collect and Why',
        paragraphs: [
          'Account and profile information: name, email, phone number, username, display name, avatar, company or workspace name, billing email, industry, company size, and website. We collect this to create and manage your account, communicate with you, and customize your workspace.',
          'Authentication and device data: password hash, OAuth provider and identifier, session tokens, IP address, browser user agent, and timestamps. We collect this to keep your account secure, verify your identity, and prevent unauthorized access.',
          'Activity logs: action type, resource, metadata, IP address, and user agent. We collect this to detect suspicious activity, support audits, debug issues, and maintain a record of who did what in your workspace.',
          'Customer Data: messages, conversations, contacts, tickets, knowledge base articles, departments, business hours, widget settings, customer invoices, and any content you or your team submit. We collect and process this because you choose to store it in your workspace so we can deliver the Service.',
          'Billing and payment data: billing email, plan, seat count, currency, transaction reference, amount, status, and payment timestamp. We collect this to process payments and generate invoices. Card details are processed by Flutterwave; we do not store full card numbers.',
          'Cookies and similar technologies: information about your device, browser, and site usage. We collect this to remember your preferences, understand how the site is used, and improve the user experience. See our Cookie Policy for more.',
        ],
      },
      {
        title: '4. How We Use Your Information',
        paragraphs: [
          'Provide and operate the Service, including workspaces, messaging, tickets, and analytics.',
          'Authenticate you and keep your account secure.',
          'Process payments and subscriptions, and generate and send invoices.',
          'Send account, billing, and security notifications.',
          'Provide customer support and respond to your questions.',
          'Detect and prevent fraud, abuse, and security incidents.',
          'Comply with legal obligations and enforce our agreements.',
          'Improve the product using aggregated, non-identifiable usage data.',
          'Customer Data is used only to deliver the workspace features you enable. We do not use your Customer Data to build general AI models or for advertising without your consent.',
          'Where AI-assisted features are available, they process your workspace data for your own use and do not train shared models.',
        ],
      },
      {
        title: '5. Legal Bases for Processing',
        paragraphs: [
          'We process your information based on one or more of the following legal grounds:',
          'Performance of a contract: to provide the Service you signed up for.',
          'Consent: where you have given clear permission, such as for optional cookies or marketing communications.',
          'Legitimate interests: to keep the Service secure, to improve our product, and to support customers.',
          'Legal obligation: to comply with applicable laws, regulations, or court orders.',
        ],
      },
      {
        title: '6. Cookies and Similar Technologies',
        paragraphs: [
          'Our website uses cookies to remember your preferences, analyze traffic, and improve the user experience.',
          'Essential cookies are needed for the site to work. Functional cookies remember your settings. Analytics cookies help us understand how the site is used. Marketing cookies help us measure and improve our advertising.',
          'You can manage your cookie preferences through the Cookie Preferences panel linked in the footer.',
        ],
      },
      {
        title: '7. How We Share Information',
        paragraphs: [
          'We do not sell or rent your personal data.',
          'We share data with service providers who help us operate the Service. These include Cloudflare for hosting and database, Flutterwave for payment processing, Infobip for email delivery, Google and LinkedIn for OAuth, and WhatsApp for messaging. These providers are contractually bound to protect your data and use it only for the agreed purpose.',
          'We may disclose information if required by law, to protect our rights, safety, or property, to prevent fraud or abuse, or in connection with a business transfer such as a merger or acquisition.',
          'Customer invoices and related data are shared only with the workspace members and customers you choose to send them to.',
        ],
      },
      {
        title: '8. How Long We Keep Your Information',
        paragraphs: [
          'We keep your personal information and Customer Data for as long as your account or workspace is active or as needed to provide the Service.',
          'Sessions are active for up to 30 days. Email verification codes expire after five minutes. Password reset tokens expire after one hour.',
          'If you request account deletion, we schedule redaction after a 30-day grace period. During that period the account is deactivated. At the end of the period we anonymize personal data by clearing or replacing fields such as display name, username, email, phone, bio, avatar, password hash, and OAuth identifiers.',
          'Billing, subscription, and transaction records are retained for legal and financial record-keeping.',
          'Customer Data in a workspace is retained according to the workspace owner settings. When a workspace is closed or deleted, we may remove or anonymize associated data, subject to legal retention requirements.',
        ],
      },
      {
        title: '9. Data Security',
        paragraphs: [
          'We use appropriate technical and organizational security measures, including encryption in transit, access controls, regular security reviews, and staff training.',
          'No system is completely secure. We cannot guarantee absolute security, but we work hard to protect your data and respond quickly to any issues.',
        ],
      },
      {
        title: '10. International Transfers',
        paragraphs: [
          'Your data may be stored and processed in data centers located outside your country, including in Africa, Europe, North America, and Asia-Pacific.',
          'We take steps to ensure that international transfers are protected through appropriate safeguards, such as standard contractual clauses and data processing agreements.',
        ],
      },
      {
        title: '11. Your Rights',
        paragraphs: [
          'Depending on your location, you may have the right to access, correct, delete, restrict, or object to processing of your personal data, and to request portability or withdraw consent.',
          'To exercise your rights or ask questions, contact us at support@zurihub.io. If your data is controlled by a business workspace, we may need to coordinate with the workspace owner or admin.',
          'We will respond within the timeframe required by applicable law.',
        ],
      },
      {
        title: '12. Children',
        paragraphs: [
          'ZuriHub is not intended for use by individuals under the age of 18, or the minimum legal age in your jurisdiction.',
          'We do not knowingly collect data from children. If you believe a child has provided us with personal data, contact us immediately.',
        ],
      },
      {
        title: '13. Changes to This Policy',
        paragraphs: [
          'We may update this Privacy Policy from time to time. We will post the revised version on this page and update the effective date.',
          'Significant changes will be communicated to you by email or through the Service.',
        ],
      },
      {
        title: '14. Contact Us',
        paragraphs: [
          'For privacy questions or data requests, contact us at support@zurihub.io or through the Book a Demo page.',
        ],
      },
    ],
  },
  cookie: {
    title: 'Cookie Policy',
    lastUpdated: 'August 7, 2026',
    paragraphs: [
      'ZuriHub uses cookies and similar technologies to provide, secure, and improve our website and platform.',
      'Essential cookies are needed for the website to work and cannot be disabled. Without these cookies, you would not be able to log in, keep a session, or use core features.',
      'Functional cookies remember choices you make, such as language or region, so you do not have to enter them again.',
      'Analytics cookies help us understand how visitors move through the site, which pages are popular, and where we can improve.',
      'Marketing cookies help us measure the performance of our advertising and show relevant messages to people who may be interested in ZuriHub.',
      'You can manage your cookie preferences at any time by clicking the Cookie Preferences button in the footer of our website.',
      'By continuing to use our site without changing your settings, you consent to the use of cookies as described in this policy.',
      'For questions, contact us at support@zurihub.io.',
    ],
  },
};
